
import React, { useEffect, useRef, useState } from 'react';
import type { RestaurateurView, Customer } from '../shared/types';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import CustomerSegments from './components/CustomerSegments';
import CustomerProfile from './components/CustomerProfile';
import POSSystem from './components/pos/POSSystem';
import RemiExpertHub from './components/RemiExpertHub';
import LoyaltyProgram from './components/LoyaltyProgram';
import MarketingCampaigns from './components/MarketingCampaigns';
import LoyaltyAnalytics from './components/LoyaltyAnalytics';
import RestaurantProfileEditor from './components/RestaurantProfileEditor';
import ReservationsManager from './components/ReservationsManager';
import StaffManager from './components/StaffManager';
import BillingManager from './components/BillingManager';
import SettingsPage from './components/SettingsPage';
import HelpPage from './components/HelpPage';
import ClientQrScanner from './components/ClientQrScanner';
import ManualValidation from './components/ManualValidation';
import RestaurateurPendingPOS from './components/RestaurateurPendingPOS';
import NfcTagsManager from './components/NfcTagsManager';
import PendingRedemptions from './components/PendingRedemptions';
import OnboardingWizard from './components/OnboardingWizard';
import { firestoreService } from '../shared/services/firestoreService';
import { USE_FIREBASE, getBackendService } from '../shared/services/apiConfig';

/** Titres d'onglet navigateur — le titre affiché appartient à chaque écran. */
const VIEW_TITLES: Record<RestaurateurView, string> = {
    dashboard: "Vue d'ensemble",
    customers: 'Clients',
    customerProfile: 'Profil client',
    reservations: 'Réservations',
    'remi-expert': 'Rémi',
    'loyalty-program': 'Programme de fidélité',
    campaigns: 'Campagnes & promos',
    analytics: 'Analyses',
    'profile-editor': 'Mon établissement',
    staff: 'Équipe',
    billing: 'Abonnement & facturation',
    settings: 'Paramètres',
    help: 'Aide & support',
    'nfc-pos': 'Encaissement fidélité',
    'nfc-tags': 'Puces NFC',
    redemptions: 'Récompenses à valider',
    pos: 'Caisse',
};

/** Compteurs "à traiter" affichés en pastille dans la navigation (Firebase uniquement). */
function usePendingCounts(restaurantId: string) {
    const [reservations, setReservations] = useState(0);
    const [redemptions, setRedemptions] = useState(0);

    useEffect(() => {
        if (!USE_FIREBASE || !restaurantId) return;
        let unsubRes: (() => void) | undefined;
        let unsubRed: (() => void) | undefined;
        try {
            unsubRes = firestoreService.subscribeToPendingReservationCount(restaurantId, setReservations);
        } catch { /* hors ligne : pas de pastille */ }
        (async () => {
            try {
                const { collection, onSnapshot, query, where } = await import('firebase/firestore');
                const { db } = await import('../shared/services/firebase');
                const q = query(
                    collection(db, 'rewardRedemptions'),
                    where('restaurantId', '==', restaurantId),
                    where('status', '==', 'pending'),
                );
                unsubRed = onSnapshot(q, (snap) => {
                    const now = Date.now();
                    setRedemptions(snap.docs.filter((d) => {
                        const exp = (d.data() as any).expiresAtMs;
                        return !exp || exp > now;
                    }).length);
                }, () => {});
            } catch { /* idem */ }
        })();
        return () => { unsubRes?.(); unsubRed?.(); };
    }, [restaurantId]);

    return { reservations, redemptions };
}

const RestaurateurApp: React.FC = () => {
    // La « Vue d'ensemble » est retirée pour l'instant : on ouvre sur le quotidien.
    const [currentView, setCurrentView] = useState<RestaurateurView>('reservations');
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | number | null>(null);
    const [showQrScanner, setShowQrScanner] = useState(false);
    const [showManualValidation, setShowManualValidation] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingChecked, setOnboardingChecked] = useState(false);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [navOpen, setNavOpen] = useState(false);

    const currentUser = USE_FIREBASE ? firestoreService.getCurrentUser() : null;
    const restaurantId = String(currentUser?.restaurantId ?? '');
    const restaurantName = currentUser?.restaurantName || currentUser?.firstName || 'Votre restaurant';
    const pending = usePendingCounts(restaurantId);

    // Changement d'écran : titre d'onglet, et focus sur le <h1> du nouvel écran (les lecteurs
    // d'écran annoncent la page ; le clavier repart du haut au lieu de rester dans la barre).
    const firstView = useRef(true);
    useEffect(() => {
        document.title = `${VIEW_TITLES[currentView] ?? 'ATABLE'} · ATABLE`;
        if (firstView.current) { firstView.current = false; return; }
        const frame = requestAnimationFrame(() => {
            const h1 = document.querySelector<HTMLElement>('main h1');
            if (!h1) return;
            h1.tabIndex = -1;
            h1.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(frame);
    }, [currentView]);

    useEffect(() => {
        if (!restaurantId || onboardingChecked) return;
        firestoreService.getRestaurant(restaurantId).then(r => {
            if (r && !r.onboardingCompleted) setShowOnboarding(true);
            setOnboardingChecked(true);
        });
    }, [restaurantId]);

    useEffect(() => {
        if (!restaurantId) return;
        let cancelled = false;
        (async () => {
            const backend = await getBackendService();
            if (!backend.getRestaurantCustomers) return;
            const data = await backend.getRestaurantCustomers(restaurantId);
            if (!cancelled) setCustomers(data);
        })().catch(() => {});
        return () => { cancelled = true; };
    }, [restaurantId]);

    if (showOnboarding && onboardingChecked) {
        return (
            <OnboardingWizard
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                onComplete={() => setShowOnboarding(false)}
            />
        );
    }

    const navigate = (view: RestaurateurView) => {
        setCurrentView(view);
        setNavOpen(false);
        // Chaque écran repart en haut de page.
        document.querySelector('main')?.scrollTo({ top: 0 });
    };

    const handleSelectCustomer = (id: string | number) => {
        setSelectedCustomerId(id);
        navigate('customerProfile');
    };

    const handleBackToCustomers = () => {
        setSelectedCustomerId(null);
        navigate('customers');
    };

    if (currentView === 'pos') {
        // « Quitter la caisse » est rendu par la caisse : désactivé pendant un encaissement.
        return <POSSystem onExit={() => navigate('reservations')} />;
    }

    const renderContent = () => {
        switch (currentView) {
            case 'dashboard':
                return <ReservationsManager onSelectCustomer={handleSelectCustomer} />;
            case 'remi-expert':
                return <RemiExpertHub />;
            case 'loyalty-program':
                return <LoyaltyProgram />;
            case 'campaigns':
                return <MarketingCampaigns />;
            case 'customers':
                return <CustomerSegments onSelectCustomer={handleSelectCustomer} onCustomersLoaded={setCustomers} />;
            case 'analytics':
                return <LoyaltyAnalytics />;
            case 'reservations':
                return <ReservationsManager onSelectCustomer={handleSelectCustomer} />;
            case 'profile-editor':
                return <RestaurantProfileEditor />;
            case 'staff':
                return <StaffManager />;
            case 'billing':
                return <BillingManager />;
            case 'settings':
                return <SettingsPage />;
            case 'help':
                return <HelpPage />;
            case 'nfc-pos':
                return <RestaurateurPendingPOS restaurantId={restaurantId} />;
            case 'nfc-tags':
                return <NfcTagsManager restaurantId={restaurantId} />;
            case 'redemptions':
                return <PendingRedemptions onSelectCustomer={handleSelectCustomer} />;
            case 'customerProfile': {
                const customer = customers.find(c => String(c.id) === String(selectedCustomerId));
                return customer
                    ? <CustomerProfile customer={customer} onBack={handleBackToCustomers} />
                    : <CustomerSegments onSelectCustomer={handleSelectCustomer} onCustomersLoaded={setCustomers} />;
            }
            default:
                return <ReservationsManager onSelectCustomer={handleSelectCustomer} />;
        }
    };

    // Les actions de validation n'ont de sens que sur les écrans d'encaissement.
    const VIEWS_WITH_NFC_ACTIONS: RestaurateurView[] = ['nfc-pos', 'redemptions'];
    const showNfcActions = VIEWS_WITH_NFC_ACTIONS.includes(currentView);

    return (
        <div className="flex h-screen h-dvh bg-cream dark:bg-gray-950 font-sans text-gray-900 dark:text-gray-100">
            <Sidebar
                currentView={currentView}
                setCurrentView={navigate}
                open={navOpen}
                onClose={() => setNavOpen(false)}
                pendingReservations={pending.reservations}
                pendingRedemptions={pending.redemptions}
            />
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <Header
                    onNavigate={(v) => navigate(v as RestaurateurView)}
                    onOpenNav={() => setNavOpen(true)}
                    navOpen={navOpen}
                    onValidationManuelle={showNfcActions ? () => setShowManualValidation(true) : undefined}
                    onScanClient={showNfcActions ? () => setShowQrScanner(true) : undefined}
                />
                {/* relative : bloc conteneur des éléments absolus des écrans (champs fichier sr-only…),
                    sinon ils agrandissent la page entière, qui défile alors sous la barre latérale. */}
                <main className="relative flex-1 overflow-x-hidden overflow-y-auto scroll-thin">
                    <div key={currentView} className="page-in max-w-[1400px] mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
                        {renderContent()}
                    </div>
                </main>
            </div>

            {showQrScanner && <ClientQrScanner onClose={() => setShowQrScanner(false)} />}
            {showManualValidation && <ManualValidation onClose={() => setShowManualValidation(false)} />}
        </div>
    );
};

export default RestaurateurApp;
